import ApiService from "../../../services/ApiService";
import { auth, LoginWithCustomToken } from "../../../services/firebase";
import { URL_PARAMS, USER_TYPES } from "../../../assets/appConstants";
import { resolveAppTypeHintFromUrlParams } from "../../../services/utils";
import { mapTherapistMeToWizardAccount } from "./registrationWizardBootstrap";
import { REGISTRATION_SCENARIOS } from "./registrationWizardConstants";
import {
  resolveClinicianLoginSession,
  parseCustomTokenResponse,
} from "../../../services/clinicianLoginSession";
import {
  isClinicianAccountType,
  resolveAccountContext,
  resolveAccountType,
} from "../../../services/accountTypeResolver";

export {
  isClinicianAccountType,
  resolveAccountType,
  getAccountTypeFromCustomClaims,
} from "../../../services/accountTypeResolver";

/** Explicit clinician registration link. Regular OTL links are login-only. */
export const isClinicianRegistrationLink = (params = {}) =>
  Boolean(params[URL_PARAMS.REGISTRATION_CODE] || params.registrationCode);

const buildWizardAccount = (accountId, accountType, wizardAccount, loginContext = {}) => ({
  accountType: loginContext.accountType || accountType || USER_TYPES.THERAPIST,
  wizardAccount: {
    ...wizardAccount,
    accountId,
    accountType: loginContext.accountType || accountType || USER_TYPES.THERAPIST,
    loginData: loginContext.customToken
      ? {
          customToken: loginContext.customToken,
        }
      : wizardAccount.loginData,
  },
});

const buildMinimalClinicianWizardAccount = (accountId, accountType, loginContext = {}) =>
  buildWizardAccount(
    accountId,
    accountType,
    mapTherapistMeToWizardAccount({}, accountId, {
      registrationScenario: REGISTRATION_SCENARIOS.SUB_ACCOUNT,
    }),
    loginContext.accountId
      ? loginContext
      : { accountId, accountType: USER_TYPES.THERAPIST }
  );

const signInWithTokenResponse = async (tokenRes) => {
  const customToken = parseCustomTokenResponse(tokenRes);
  if (!customToken) {
    return null;
  }
  await LoginWithCustomToken(customToken);
  return {
    ...(typeof tokenRes === "object" && tokenRes ? tokenRes : {}),
    customToken,
  };
};

/**
 * Exchange an invitation OTL for a Firebase custom token via
 * POST /v2/accounts/custom-token-by-otl/ with body `{ code }` only.
 */
export const exchangeOtlAndSignIn = async (otlCode) => {
  const v2Res = await ApiService.getCustomTokenByOtl(otlCode);
  return signInWithTokenResponse(v2Res);
};

/** Registration codes use the same one-time-code exchange endpoint. */
export const exchangeRegistrationCodeAndSignIn = (registrationCode) =>
  exchangeOtlAndSignIn(registrationCode);

/**
 * After OTL sign-in, complete regular login. Patient behavior remains legacy login-helper.
 */
export const handlePostOtlAuth = async (tokenRes, urlParams = {}) => {
  const user = auth.currentUser;
  if (!user) {
    throw new Error("No authenticated user after OTL sign-in");
  }

  const accountTypeHint = resolveAppTypeHintFromUrlParams(urlParams);
  const accountContext = await resolveAccountContext(user, accountTypeHint);
  const accountType = accountContext.accountType;

  if (isClinicianAccountType(accountType)) {
    const session = await resolveClinicianLoginSession(user, {
      accountTypeHint,
      accountContext,
      customToken: tokenRes.customToken,
      forceRegularLogin: true,
      forceTherapistMe: true,
    });

    return {
      action: "finishLogin",
      loginData: session.loginData,
    };
  }

  const idToken = await user.getIdToken();
  const loginRes = await ApiService.loginHelper(user.uid, idToken);
  const needsPatientRegistration =
    loginRes?.accountData && loginRes.registrationCompleted !== true;

  if (needsPatientRegistration) {
    return {
      action: "finishLogin",
      loginData: {
        ...loginRes,
        accountId: user.uid,
        customToken: tokenRes.customToken,
      },
    };
  }

  return {
    action: "finishLogin",
    loginData: {
      ...loginRes,
      accountId: user.uid,
      customToken: tokenRes.customToken,
      accountData: null,
      immediateRedirect: true,
    },
  };
};

/**
 * Bootstrap clinician wizard state from GET /v2/therapists/account/for-registration.
 * Requires a sub-account Firebase token. Do not call /v2/therapists/me here.
 * Pass `registrationDto` when already fetched to avoid a second GET.
 */
export const bootstrapClinicianRegistration = async (accountId, options = {}) => {
  const { accountType: typeHint, loginRes, user, registrationDto: prefetched } = options;
  const accountType =
    typeHint ||
    (await resolveAccountType(user || auth.currentUser, loginRes?.accountType));
  const loginAccount = loginRes?.accountData || {};
  const loginContext = loginRes || { accountId, accountType };

  const mapRegistration = (dto) =>
    mapTherapistMeToWizardAccount(dto, accountId, loginAccount);

  if (prefetched) {
    return buildWizardAccount(accountId, accountType, mapRegistration(prefetched), loginContext);
  }

  try {
    const registration = await ApiService.getTherapistForRegistration();
    if (registration) {
      return buildWizardAccount(
        accountId,
        accountType,
        mapRegistration(registration),
        loginContext
      );
    }
  } catch (e) {
    ApiService.logDispatcherError("v2 for-registration unavailable during clinician bootstrap", e);
  }

  if (loginAccount.accountId || loginAccount.subAccountId || loginAccount.firstName) {
    return buildWizardAccount(accountId, accountType, loginAccount, loginContext);
  }

  if (isClinicianAccountType(accountType)) {
    return buildMinimalClinicianWizardAccount(accountId, accountType, loginContext);
  }

  if (loginRes) {
    const wizardAccount = mapTherapistMeToWizardAccount({}, accountId, {
      ...loginAccount,
      registrationScenario: options.registrationScenario,
    });
    return buildWizardAccount(
      accountId,
      accountType,
      {
        ...loginAccount,
        ...wizardAccount,
      },
      loginContext
    );
  }

  throw new Error("clinician bootstrap unavailable");
};
